<template>
    <details class="allocation-billing" @toggle="expanded = $event.target.open">
        <summary class="allocation-billing-heading">
            <div>
                <h2>Resource-pool billing</h2>
                <p>Recorded reservations of the sessions each pool was billed to, not the worker capacity those sessions ran on.</p>
            </div>
            <v-chip small outlined>{{ billing.sessionCount }} sessions</v-chip>
            <v-icon small class="allocation-billing-chevron">mdi-chevron-down</v-icon>
        </summary>
        <div v-if="expanded" class="allocation-billing-scroll">
            <table class="allocation-billing-table">
                <thead>
                    <tr>
                        <th scope="col">Resource pool</th>
                        <th scope="col">Sessions</th>
                        <th v-for="metric in billing.resources" :key="metric.key" scope="col">
                            {{ metric.label }}
                            <small>{{ metric.unit }}</small>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr class="allocation-billing-total">
                        <td>
                            <strong>Subtree total</strong>
                            <small>{{ billing.rootLabel }} and descendants · reserved / limit</small>
                        </td>
                        <td>{{ billing.subtree.sessionCount }}</td>
                        <td v-for="metric in billing.resources" :key="metric.key" :title="warning(billing.subtree, metric.key)">
                            {{ measurement(billing.subtree, metric.key) }}
                        </td>
                    </tr>
                    <template v-for="row in billing.rows">
                        <tr :key="row.rpid" :class="{ 'allocation-billing-selected': row.isSelected }">
                            <td>
                                <span class="allocation-billing-entity" :style="{ paddingLeft: row.depth * 16 + 'px' }">
                                    <strong>{{ row.label }}</strong>
                                    <small>{{ entityLabel(row) }}</small>
                                </span>
                            </td>
                            <td>{{ row.direct.sessionCount }}</td>
                            <td v-for="metric in billing.resources" :key="metric.key" :title="warning(row.direct, metric.key)">
                                {{ measurement(row.direct, metric.key) }}
                            </td>
                        </tr>
                        <tr v-if="row.subtree && !row.isSelected" :key="row.rpid + '-subtree'" class="allocation-billing-subtree">
                            <td>
                                <span class="allocation-billing-entity" :style="{ paddingLeft: (row.depth + 1) * 16 + 'px' }">
                                    <strong>Subtree</strong>
                                    <small>{{ row.label }} and descendants</small>
                                </span>
                            </td>
                            <td>{{ row.subtree.sessionCount }}</td>
                            <td v-for="metric in billing.resources" :key="metric.key" :title="warning(row.subtree, metric.key)">
                                {{ measurement(row.subtree, metric.key) }}
                            </td>
                        </tr>
                    </template>
                </tbody>
            </table>
        </div>
        <p v-if="expanded && billing.unattributedSessions" class="allocation-note">
            {{ billing.unattributedSessions }} sessions in the window have no recorded billing pool and are not shown above.
        </p>
    </details>
</template>

<script>
import { formatResourceMeasurement, resourceMeasurementWarning } from './nodeAllocationData'

export default {
    name: 'NodeAllocationBillingBreakdown',
    props: {
        billing: { type: Object, required: true }
    },
    data() {
        return { expanded: false }
    },
    methods: {
        entityLabel(row) {
            if (row.isSelected) return 'Selected pool · direct billing'
            return row.depth === 1 ? 'Child pool' : 'Descendant pool'
        },
        metric(scope, key) {
            return scope.resources.find(item => item.key === key) || null
        },
        measurement(scope, key) {
            const metric = this.metric(scope, key)
            if (!metric || scope.sessionCount === 0) return '—'
            const requests = formatResourceMeasurement({ value: metric.requested, status: metric.requestStatus }, metric.unit)
            const limits = formatResourceMeasurement({ value: metric.limit, status: metric.limitStatus }, metric.unit)
            return `${requests} / ${limits}`
        },
        warning(scope, key) {
            const metric = this.metric(scope, key)
            if (!metric || scope.sessionCount === 0) return ''
            return [
                ...new Set(
                    ['requests', 'limits']
                        .map(kind =>
                            resourceMeasurementWarning(
                                {
                                    value: kind === 'requests' ? metric.requested : metric.limit,
                                    status: kind === 'requests' ? metric.requestStatus : metric.limitStatus
                                },
                                kind
                            )
                        )
                        .filter(Boolean)
                )
            ].join('. ')
        }
    }
}
</script>

<style scoped>
.allocation-billing {
    margin: 18px 0;
    border: 1px solid #e0e7ef;
    border-radius: 8px;
    background: white;
    padding: 16px 20px;
}
.allocation-billing-heading {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    flex-wrap: wrap;
    list-style: none;
    cursor: pointer;
}
.allocation-billing-heading::-webkit-details-marker {
    display: none;
}
.allocation-billing-chevron {
    transition: transform 0.2s;
}
.allocation-billing[open] .allocation-billing-chevron {
    transform: rotate(180deg);
}
.allocation-billing-heading h2 {
    font-size: 17px;
    font-weight: 600;
    margin: 0;
}
.allocation-billing-heading p {
    color: #64748b;
    font-size: 12px;
    line-height: 1.7;
    margin: 6px 0 0;
}
.allocation-billing small {
    color: #64748b;
    font-size: 11px;
    line-height: 1.6;
}
.allocation-billing-scroll {
    max-height: 360px;
    overflow: auto;
    margin-top: 12px;
}
.allocation-billing-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 12px;
}
.allocation-billing-table thead th {
    position: sticky;
    top: 0;
    z-index: 1;
    background: white;
}
.allocation-billing-table th,
.allocation-billing-table td {
    padding: 8px;
    text-align: left;
    border-bottom: 1px solid #e0e7ef;
    white-space: nowrap;
}
.allocation-billing-table th small {
    display: block;
}
.allocation-billing-entity strong,
.allocation-billing-entity small {
    display: block;
}
.allocation-billing-selected {
    background: #f5f9ff;
}
.allocation-billing-total {
    font-weight: 500;
    background: #f8fafc;
}
.allocation-billing-subtree td {
    color: #64748b;
}
</style>
